import {
  editorialInfoTypeName,
  memoNaming,
  resolveMemoDates,
  resolveMemoSections
} from './resolveMemoTitle.js'
import { slugify } from './slugify.js'
import type { MemoTemplate, MemoVariant } from './schema.js'

/**
 * `date` and `toDate` are the span the memo covers, for a slugline that names
 * it - "TT-fredag-helg-pm 31.7-2.8", or the day it covers - "TT-torsdag-pm 3.9".
 * A slugline with no date placeholder needs neither.
 *
 * `sectionTitle` is the section the memo is written for, the way it is for a
 * title - "TT-utrikes-vecko-pm" for a vecko-PM written for Utrikes.
 */
export interface MemoSluglineOptions {
  editorialInfoTypeTitle?: string
  date?: Date
  toDate?: Date
  sectionTitle?: string
  variant?: MemoVariant
  localeCode?: string
}

/**
 * Resolve a template's suggested slugline.
 *
 * The same date placeholders a title pattern understands are resolved here, so
 * a slugline can name the span it covers. They are only substituted when the
 * caller passes a date; a slugline naming one without a date to resolve it
 * against is left as it is rather than turned into a half filled string.
 *
 * A template with variants takes the selected variant's slugline instead of its
 * own - the weekend PM is "TT-fredag-helg-pm" or "TT-helg-måndag-pm" - and one
 * with a per-section naming takes that when the memo is written for a section,
 * the same way its title does. See `memoNaming`.
 *
 * Templates that require an editorial-info type (Print-PM) get the chosen
 * type's title appended as a suffix, e.g. "print-pm" + "BNL" ->
 * "print-pm-bnl": which type the PM is about is part of what a Print-PM
 * slugline names, since the desk writes one per type on the same day. Every
 * other template just uses its configured base slugline as-is.
 *
 * Editorial-info-type titles are themselves named with a "PM " prefix (e.g.
 * "PM BNL", "PM Spel"), which is stripped before slugifying - otherwise it
 * would double up with the "pm" the base ends in, into "print-pm-pm-bnl".
 */
export function resolveMemoSlugline(
  template: MemoTemplate,
  options: MemoSluglineOptions = {}
): string {
  const { editorialInfoTypeTitle, date, toDate, sectionTitle, variant, localeCode } = options
  const naming = memoNaming(template, { sectionTitle, variant })
  const pattern = resolveMemoSections(naming.slugline, sectionTitle, localeCode)
  const base = date
    ? resolveMemoDates(pattern, { date, toDate, localeCode }).replace(/\s+/g, ' ').trim()
    : pattern

  if (!template.requiresEditorialInfoType || !editorialInfoTypeTitle) {
    return base
  }

  const suffix = slugify(editorialInfoTypeName(editorialInfoTypeTitle))
  return suffix ? `${base}-${suffix}` : base
}

/**
 * Resolve the slugline suggested for a memo's assignment, which is the
 * planning's unless the template declares `planning.assignmentSluglinePattern`,
 * e.g. "TT-inrikes-vecko-pmv40". That pattern names no section,
 * so it reads the same whichever section the memo is written for.
 */
export function resolveMemoAssignmentSlugline(
  template: MemoTemplate,
  options: MemoSluglineOptions = {}
): string {
  const { assignmentSluglinePattern } = template.planning
  const { date, toDate, localeCode } = options

  if (!assignmentSluglinePattern || !date) {
    return resolveMemoSlugline(template, options)
  }

  return resolveMemoDates(assignmentSluglinePattern, { date, toDate, localeCode })
    .replace(/\s+/g, ' ')
    .trim()
}
